/* =====================================================================
   TEGO-CFG 1 · el protocolo de configuración por Bluetooth, sin React
   =====================================================================
   Contrato de la terminal v2 2.5.0 §16 (lo definió y probó el firmware:
   `configble.h/.cpp`). Este archivo es PURO: bytes, llaves y textos. No
   toca el navegador salvo por `crypto.subtle`, que se pasa o se toma del
   global, así que se prueba con `node:test` y la Web Crypto de Node, que
   tiene X25519.

     · fragmentar / Reensamblador — trozos de 20 B, bit 7 = último (§16.2)
     · derivarLlave — X25519 + HKDF-SHA256 → AES-128-GCM (§16.4)
     · cifrar / descifrar — iv (12 B) ‖ AES-GCM, dato asociado = [tipo] (§16.3)
     · mensajes de la página, redes (0x83) y estados con su texto (§16.5)

   LA CONTRASEÑA DEL WI-FI DEL CLIENTE solo existe aquí dentro, en el
   mensaje WIFI cifrado para la terminal: nunca va al servidor (§16.5).
   ===================================================================== */

export const UUID = {
  servicio: '7e1a0001-3b5c-4d8e-9f20-54e60c7a1b00',
  info: '7e1a0002-3b5c-4d8e-9f20-54e60c7a1b00',
  comando: '7e1a0003-3b5c-4d8e-9f20-54e60c7a1b00',
  estado: '7e1a0004-3b5c-4d8e-9f20-54e60c7a1b00',
  respuesta: '7e1a0005-3b5c-4d8e-9f20-54e60c7a1b00',
};

export const TIPO = {
  HOLA: 0x01, CONFIRMAR: 0x02, REDES: 0x03, WIFI: 0x04, CREDENCIAL: 0x05,
  IDENTIFICAR: 0x06, TERMINAR: 0x07, RESP_REDES: 0x83,
};

export const ESTADO = {
  ESPERANDO_PAGINA: 0x01, ESPERANDO_CODIGO: 0x02, LISTA: 0x03, CONECTANDO: 0x04,
  CONECTADA: 0x05, CODIGO_VINCULAR: 0x06, VINCULADA: 0x07, ERROR: 0x10,
};

export const FRAGMENTO = 20;
export const MENSAJE_MAX = 1280;
export const PREFIJO_NOMBRE = 'TEGO-';

const te = new TextEncoder();
const td = new TextDecoder();

function subtleDe(subtle) {
  const s = subtle || globalThis.crypto?.subtle;
  if (!s) throw new Error('Este navegador no puede cifrar la conexión.');
  return s;
}

export function concatenar(...partes) {
  const total = partes.reduce((n, p) => n + p.length, 0);
  const salida = new Uint8Array(total);
  let i = 0;
  for (const p of partes) { salida.set(p, i); i += p.length; }
  return salida;
}

export function aBytes(x) {
  if (x instanceof Uint8Array) return x;
  if (x instanceof ArrayBuffer) return new Uint8Array(x);
  if (ArrayBuffer.isView(x)) return new Uint8Array(x.buffer, x.byteOffset, x.byteLength);
  if (typeof x === 'string') return te.encode(x);
  return new Uint8Array(x || []);
}

export function base64aBytes(b64) {
  const bin = atob(String(b64 || ''));
  const salida = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) salida[i] = bin.charCodeAt(i);
  return salida;
}

export function bytesABase64(bytes) {
  let bin = '';
  for (const b of aBytes(bytes)) bin += String.fromCharCode(b);
  return btoa(bin);
}

// ---------------------------------------------------------------------
// §16.2 · Fragmentos de 20 B: byte 0 = número (bits 0–6) y bit 7 = último
// ---------------------------------------------------------------------
export function fragmentar(mensaje) {
  const m = aBytes(mensaje);
  if (m.length === 0) throw new Error('Mensaje vacío.');
  if (m.length > MENSAJE_MAX) throw new Error('Mensaje demasiado largo para la terminal.');
  const carga = FRAGMENTO - 1;
  const n = Math.ceil(m.length / carga);
  const trozos = [];
  for (let i = 0; i < n; i += 1) {
    const cabeza = (i & 0x7f) | (i === n - 1 ? 0x80 : 0);
    trozos.push(concatenar(Uint8Array.of(cabeza), m.subarray(i * carga, (i + 1) * carga)));
  }
  return trozos;
}

/** Junta los fragmentos de RESPUESTA (o de COMANDO, del lado terminal).
 *  `agregar` devuelve el mensaje completo al llegar el último, si no null.
 *  Un fragmento 0 descarta lo que hubiera a medias; uno fuera de orden
 *  descarta todo (la terminal hace lo mismo). */
export class Reensamblador {
  constructor() { this.partes = []; this.siguiente = 0; }

  agregar(fragmento) {
    const f = aBytes(fragmento);
    if (f.length < 1) return null;
    const numero = f[0] & 0x7f;
    const ultimo = (f[0] & 0x80) !== 0;
    if (numero === 0) { this.partes = []; this.siguiente = 0; }
    if (numero !== this.siguiente) { this.partes = []; this.siguiente = 0; return null; }
    this.partes.push(f.subarray(1));
    this.siguiente += 1;
    if (!ultimo) return null;
    const m = concatenar(...this.partes);
    this.partes = []; this.siguiente = 0;
    return m.length > MENSAJE_MAX ? null : m;
  }
}

// ---------------------------------------------------------------------
// §16.4 · Llaves
// ---------------------------------------------------------------------
export async function generarPar(subtle) {
  const s = subtleDe(subtle);
  const par = await s.generateKey({ name: 'X25519' }, true, ['deriveBits']);
  const publica = new Uint8Array(await s.exportKey('raw', par.publicKey));
  return { privada: par.privateKey, publica };
}

/** clave = HKDF-SHA256(X25519(privada, pública_terminal), sal = serie,
 *  info = "TEGO-CFG-1" ‖ pública_terminal ‖ pública_página), 16 B. */
export async function derivarLlave({ privada, publicaTerminal, publicaPagina, serie, subtle }) {
  const s = subtleDe(subtle);
  const pt = aBytes(publicaTerminal);
  if (pt.length !== 32) throw new Error('La llave de la terminal no tiene 32 bytes.');
  const otra = await s.importKey('raw', pt, { name: 'X25519' }, false, []);
  const compartido = await s.deriveBits({ name: 'X25519', public: otra }, privada, 256);
  const base = await s.importKey('raw', compartido, 'HKDF', false, ['deriveKey']);
  return s.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: te.encode(serie),
      info: concatenar(te.encode('TEGO-CFG-1'), pt, aBytes(publicaPagina)) },
    base, { name: 'AES-GCM', length: 128 }, false, ['encrypt', 'decrypt']);
}

// ---------------------------------------------------------------------
// §16.3 · iv (12 B al azar) ‖ AES-128-GCM(datos, dato asociado = [tipo])
// ---------------------------------------------------------------------
export async function cifrar(llave, tipo, datos, subtle) {
  const s = subtleDe(subtle);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const ct = await s.encrypt({ name: 'AES-GCM', iv, additionalData: Uint8Array.of(tipo) },
    llave, aBytes(datos));
  return concatenar(iv, new Uint8Array(ct));
}

export async function descifrar(llave, tipo, sobre, subtle) {
  const s = subtleDe(subtle);
  const b = aBytes(sobre);
  if (b.length < 12 + 16) throw new Error('Mensaje cifrado incompleto.');
  const claro = await s.decrypt({ name: 'AES-GCM', iv: b.subarray(0, 12),
    additionalData: Uint8Array.of(tipo) }, llave, b.subarray(12));
  return new Uint8Array(claro);
}

// ---------------------------------------------------------------------
// Mensajes de la página
// ---------------------------------------------------------------------
export const msgHola = (publicaPagina) => concatenar(Uint8Array.of(TIPO.HOLA), aBytes(publicaPagina));
export const msgSinDatos = (tipo) => Uint8Array.of(tipo);

export async function msgCifrado(llave, tipo, datos, subtle) {
  return concatenar(Uint8Array.of(tipo), await cifrar(llave, tipo, datos, subtle));
}

export const soloNumeros = (texto) => String(texto || '').replace(/\D/g, '').slice(0, 4);

export function largoUtf8(texto) { return te.encode(String(texto ?? '')).length; }

/** Regla de §16.3: vacía (red abierta) o de 8 a 63. Devuelve el motivo en
 *  palabras simples, o null si sirve. */
export function motivoDeContrasena(clave) {
  const n = largoUtf8(clave);
  if (n === 0 || (n >= 8 && n <= 63)) return null;
  if (n < 8) return `La contraseña del Wi-Fi lleva al menos 8 letras o números (van ${n}).`;
  return 'La contraseña del Wi-Fi lleva como máximo 63 letras o números.';
}

export function motivoDeRed(red) {
  const n = largoUtf8(red);
  if (n === 0) return 'Elige tu red Wi-Fi de la lista.';
  if (n > 32) return 'El nombre de esa red es demasiado largo para la terminal.';
  return null;
}

/** `[n][red][n][contraseña][n][dirección de TEGO o vacío]` */
export function datosWifi(red, clave, direccion = '') {
  const partes = [red, clave, direccion].map((t) => te.encode(String(t ?? '')));
  if (partes.some((p) => p.length > 255)) throw new Error('Dato demasiado largo.');
  return concatenar(...partes.flatMap((p) => [Uint8Array.of(p.length), p]));
}

/** Del lado terminal (y de las pruebas): lee `datosWifi`. */
export function leerDatosWifi(bytes) {
  const b = aBytes(bytes);
  const salida = [];
  let i = 0;
  for (let k = 0; k < 3; k += 1) {
    const n = b[i]; i += 1;
    if (n === undefined || i + n > b.length) throw new Error('WIFI mal formado.');
    salida.push(td.decode(b.subarray(i, i + n))); i += n;
  }
  return { red: salida[0], clave: salida[1], direccion: salida[2] };
}

// ---------------------------------------------------------------------
// Respuesta 0x83 · redes y ESTADO
// ---------------------------------------------------------------------
/** `[0x83][n]` y por red `[rssi con signo][1 = con contraseña][largo][nombre]`. */
export function parsearRedes(bytes) {
  const b = aBytes(bytes);
  if (b[0] !== TIPO.RESP_REDES) throw new Error('No es una lista de redes.');
  const n = b[1] || 0;
  const redes = [];
  let i = 2;
  for (let k = 0; k < n && i + 3 <= b.length; k += 1) {
    const rssi = b[i] > 127 ? b[i] - 256 : b[i];
    const conClave = b[i + 1] === 1;
    const largo = b[i + 2];
    i += 3;
    if (i + largo > b.length) break;
    const nombre = td.decode(b.subarray(i, i + largo));
    i += largo;
    if (nombre) redes.push({ nombre, rssi, conClave });
  }
  return redes;
}

export function redesABytes(redes) {
  const lista = (redes || []).slice(0, 12);
  return concatenar(Uint8Array.of(TIPO.RESP_REDES, lista.length), ...lista.map((r) => {
    const nombre = te.encode(r.nombre);
    return concatenar(Uint8Array.of(r.rssi & 0xff, r.conClave ? 1 : 0, nombre.length), nombre);
  }));
}

/** ESTADO = `[estado][detalle][texto UTF-8 ≤ 18 B]`. */
export function parsearEstado(bytes) {
  const b = aBytes(bytes);
  return { estado: b[0] ?? 0, detalle: b[1] ?? 0, texto: td.decode(b.subarray(2)).replace(/\0+$/, '') };
}

export function estadoABytes(estado, detalle = 0, texto = '') {
  return concatenar(Uint8Array.of(estado, detalle), te.encode(String(texto)).subarray(0, 18));
}

/** Fuerza de la señal en palabras, para la lista de redes. */
export function fuerzaDeSenal(rssi) {
  if (rssi >= -60) return 'Señal fuerte';
  if (rssi >= -75) return 'Señal media';
  return 'Señal débil';
}

// ---------------------------------------------------------------------
// §16.5 · Lo que la página dice. En neutro (Doctrina 2, punto 13).
// ---------------------------------------------------------------------
export function textoDeEstado({ estado, texto }) {
  switch (estado) {
    case ESTADO.ESPERANDO_PAGINA: return 'Conectando con la terminal…';
    case ESTADO.ESPERANDO_CODIGO: return 'Escribe los 4 números que muestra la terminal';
    case ESTADO.LISTA: return 'Elige tu red Wi-Fi';
    case ESTADO.CONECTANDO: return `La terminal se está conectando a «${texto}»…`;
    case ESTADO.CONECTADA: return 'La terminal ya tiene internet';
    case ESTADO.CODIGO_VINCULAR: return `Vincula la terminal con el código ${texto}`;
    case ESTADO.VINCULADA: return `¡Lista! Vinculada a «${texto}»`;
    default: return '';
  }
}

export const TEXTO_DE_ERROR = {
  0x01: 'Esos números no coinciden. Revisa la pantalla de la terminal.',
  0x02: 'Demasiados intentos. La terminal muestra números nuevos.',
  0x03: 'Algo se perdió en el camino. Intenta otra vez.',
  0x04: 'Primero escribe los 4 números de la terminal.',
  0x05: 'La terminal no ve esa red. Acércala o revisa el nombre.',
  0x06: 'No se pudo conectar. Revisa la contraseña.',
  0x07: 'La terminal tiene Wi-Fi pero no llega a TEGO. Revisa que la red tenga internet.',
  0x08: 'Esa credencial no sirve. Vuelve a vincular.',
  0x09: 'La terminal no pudo guardar. Pide ayuda a TI.',
  0x0a: 'Se acabaron los 10 minutos. Sostén el botón 5 s para empezar otra vez.',
  0x0b: 'Revisa los datos: la contraseña lleva al menos 8 letras o números.',
};

export function textoDeError({ detalle, texto }) {
  if (detalle === 0x0c) return `${(texto || '').trim() || 'La terminal recibió un aviso de TEGO.'} Pide ayuda a TI.`;
  return TEXTO_DE_ERROR[detalle] || 'Algo se perdió en el camino. Intenta otra vez.';
}

/** Los 4 últimos de la serie, como los dice la pantalla (`TEGO-XXXX`). */
export function nombreDeRed(serie) {
  return `${PREFIJO_NOMBRE}${String(serie || '').slice(-4).toUpperCase()}`;
}

/** ¿Web Crypto tiene X25519? Chrome y Edge lo traen desde la 133; antes
 *  hay Web Bluetooth pero no se puede cifrar la conexión (auditoría BLE
 *  P1-1). Prueba barata y en caché, por la función que la contesta: si
 *  alguien cambia `generateKey` (una prueba), se vuelve a mirar. */
let cacheX25519 = null;
export async function hayX25519(subtle) {
  const s = subtle || globalThis.crypto?.subtle;
  if (!s?.generateKey) return false;
  if (cacheX25519 && cacheX25519.fn === s.generateKey) return cacheX25519.si;
  let si = false;
  try {
    await s.generateKey({ name: 'X25519' }, false, ['deriveBits']);
    si = true;
  } catch {
    si = false;
  }
  cacheX25519 = { fn: s.generateKey, si };
  return si;
}

/** ¿Se puede configurar por Bluetooth? `navigator.bluetooth`,
 *  `getAvailability()` verdadero y X25519 en Web Crypto. */
export async function hayBluetooth(nav = globalThis.navigator, subtle = undefined) {
  try {
    if (!nav?.bluetooth?.requestDevice) return false;
    if (typeof nav.bluetooth.getAvailability !== 'function') return false;
    if ((await nav.bluetooth.getAvailability()) !== true) return false;
    return await hayX25519(subtle);
  } catch {
    return false;
  }
}
